import { useState, useRef, useEffect } from 'react';
import { Search, Check, X, ChevronDown } from 'lucide-react';
import './datatable.css';

export interface ACMultiSelectOption {
  id: string;
  nome: string;
  subtitulo?: string;
  fotoUrl?: string | null;
  perfil?: string;
  cor?: string;
  presence?: 'online' | 'ocioso' | 'offline';
}

export interface ACMultiSelectDropdownProps {
  options: ACMultiSelectOption[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  showAvatars?: boolean;
  showPresence?: boolean;
  width?: string | number;
  maxVisible?: number;
  align?: 'left' | 'right';
  /** When true, only one option can be selected at a time (radio behavior).
   *  Clicking selects that option and deselects all others. Closes on select. */
  singleSelect?: boolean;
}

export function ACMultiSelectDropdown({
  options,
  selectedIds,
  onChange,
  placeholder = 'Selecione...',
  searchPlaceholder = 'Buscar...',
  showAvatars = true,
  showPresence = false,
  width = '100%',
  maxVisible = 6,
  align = 'left',
  singleSelect = false,
}: ACMultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Close dropdown on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // Focus search input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 60);
    }
  }, [isOpen]);

  const handleToggle = (id: string) => {
    if (singleSelect) {
      // Radio behavior: select this one, deselect all others
      const next = selectedIds.includes(id) ? [] : [id];
      onChange(next);
      setIsOpen(false);
      setSearch('');
      return;
    }
    const next = selectedIds.includes(id)
      ? selectedIds.filter((x) => x !== id)
      : [...selectedIds, id];
    onChange(next);
  };

  const handleSelectAll = () => {
    const allFilteredIds = filteredOptions.map((o) => o.id);
    const next = Array.from(new Set([...selectedIds, ...allFilteredIds]));
    onChange(next);
  };

  const handleClearAll = () => {
    const allFilteredIds = filteredOptions.map((o) => o.id);
    const next = selectedIds.filter((id) => !allFilteredIds.includes(id));
    onChange(next);
  };

  const filteredOptions = options.filter((o) => {
    const query = search.toLowerCase();
    return (
      o.nome.toLowerCase().includes(query) ||
      (o.subtitulo && o.subtitulo.toLowerCase().includes(query))
    );
  });

  const getInitials = (name: string) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length > 1) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return parts[0][0].toUpperCase();
  };

  return (
    <div
      className="dd-wrapper"
      ref={containerRef}
      style={{ width, position: 'relative' }}
    >
      {/* Trigger Button */}
      <button
        type="button"
        className={`dd-trigger ${isOpen ? 'dd-trigger-open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        style={{
          minHeight: 38,
          borderRadius: 10,
          border: '1px solid var(--border-color, #e2e8f0)',
          background: 'var(--bg-card, #fff)',
          color: 'var(--text-main, #0f172a)',
          padding: '6px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          width: '100%',
          outline: 'none',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: '90%', alignItems: 'center' }}>
          {selectedIds.length === 0 ? (
            <span style={{ color: 'var(--text-muted, #94a3b8)', fontSize: '0.78rem' }}>
              {placeholder}
            </span>
          ) : singleSelect ? (() => {
            const selOpt = options.find(o => o.id === selectedIds[0]);
            const selCor = selOpt?.cor;
            return selCor ? (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: '0.74rem', fontWeight: 700,
                padding: '2px 10px 2px 8px', borderRadius: 20,
                background: `${selCor}18`, color: selCor,
                border: `1.5px solid ${selCor}30`,
              }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: selCor, flexShrink: 0 }} />
                {selOpt?.nome || selectedIds[0]}
              </span>
            ) : (
              <span style={{ fontSize: '0.78rem', fontWeight: 700 }}>
                {selOpt?.nome || selectedIds[0]}
              </span>
            );
          })() : (
            <span style={{ fontSize: '0.78rem', fontWeight: 700 }}>
              {selectedIds.length} selecionado{selectedIds.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {selectedIds.length > 0 && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                onChange([]);
              }}
              style={{
                color: 'var(--text-muted, #94a3b8)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                padding: 2,
              }}
              title="Limpar todos"
            >
              <X size={12} />
            </span>
          )}
          <ChevronDown size={14} style={{ color: 'var(--text-muted, #94a3b8)', transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'none' }} />
        </div>
      </button>

      {/* Popover Dropdown Panel */}
      {isOpen && (
        <div
          className={`dd-panel dd-align-${align}`}
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: align === 'left' ? 0 : 'auto',
            right: align === 'right' ? 0 : 'auto',
            width: 290,
            background: 'var(--bg-card, #fff)',
            border: '1px solid var(--border-color, #e2e8f0)',
            borderRadius: 12,
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
            zIndex: 999,
            padding: 8,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          {/* Search bar inside panel */}
          <div style={{ position: 'relative', width: '100%' }}>
            <Search
              size={12}
              style={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--text-muted, #94a3b8)',
              }}
            />
            <input
              ref={searchInputRef}
              type="text"
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="form-input"
              style={{
                width: '100%',
                padding: '5px 8px 5px 26px',
                fontSize: '0.74rem',
                borderRadius: 8,
                border: '1px solid var(--border-color, #e2e8f0)',
                background: 'var(--bg-body, #f8fafc)',
                color: 'var(--text-main, #0f172a)',
                outline: 'none',
              }}
            />
          </div>

          {/* Quick Select Actions (hidden in singleSelect mode) */}
          {!singleSelect && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid var(--border-color, #e2e8f0)',
                paddingBottom: 4,
                fontSize: '0.66rem',
                fontWeight: 800,
                color: 'var(--text-muted, #94a3b8)',
              }}
            >
              <span>{filteredOptions.length} itens encontrados</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={handleSelectAll}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#8b5cf6',
                    cursor: 'pointer',
                    fontWeight: 800,
                    fontSize: '0.66rem',
                  }}
                >
                  Todos
                </button>
                <span style={{ opacity: 0.3 }}>|</span>
                <button
                  type="button"
                  onClick={handleClearAll}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#ef4444',
                    cursor: 'pointer',
                    fontWeight: 800,
                    fontSize: '0.66rem',
                  }}
                >
                  Nenhum
                </button>
              </div>
            </div>
          )}

          {/* Options List */}
          <div
            style={{
              maxHeight: maxVisible * 36,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
            className="custom-scroll"
          >
            {filteredOptions.length === 0 ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '16px 0',
                  color: 'var(--text-muted, #94a3b8)',
                  fontSize: '0.74rem',
                }}
              >
                Nenhum resultado
              </div>
            ) : (
              filteredOptions.map((opt) => {
                const isSelected = selectedIds.includes(opt.id);
                const initials = getInitials(opt.nome);

                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => handleToggle(opt.id)}
                    style={{
                      width: '100%',
                      background: isSelected ? 'var(--bg-body, rgba(0,0,0,0.02))' : 'transparent',
                      border: 'none',
                      borderRadius: 8,
                      padding: '5px 8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.background = 'var(--bg-body, rgba(0,0,0,0.02))';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                  >
                    {/* Checkbox / Radio indicator */}
                    <div
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: singleSelect ? '50%' : 4,
                        border: '1.5px solid var(--border-color, #cbd5e1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: isSelected ? '#8b5cf6' : 'transparent',
                        borderColor: isSelected ? '#8b5cf6' : 'var(--border-color, #cbd5e1)',
                        color: 'white',
                        flexShrink: 0,
                      }}
                    >
                      {isSelected && (singleSelect
                        ? <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />
                        : <Check size={10} strokeWidth={3} />
                      )}
                    </div>

                    {/* Avatar circle if showAvatars */}
                    {showAvatars && (
                      <div
                        style={{
                          position: 'relative',
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          flexShrink: 0,
                        }}
                      >
                        {opt.fotoUrl ? (
                          <img
                            src={opt.fotoUrl}
                            alt={opt.nome}
                            style={{
                              width: '100%',
                              height: '100%',
                              borderRadius: '50%',
                              objectFit: 'cover',
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: '100%',
                              height: '100%',
                              borderRadius: '50%',
                              background: opt.cor || 'linear-gradient(135deg, #8b5cf6, #3b82f6)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.58rem',
                              color: '#fff',
                              fontWeight: 800,
                            }}
                          >
                            {initials}
                          </div>
                        )}

                        {/* Presence badge */}
                        {showPresence && opt.presence && (
                          <div
                            style={{
                              position: 'absolute',
                              bottom: -1,
                              right: -1,
                              width: 7,
                              height: 7,
                              borderRadius: '50%',
                              background:
                                opt.presence === 'online'
                                  ? '#10b981'
                                  : opt.presence === 'ocioso'
                                  ? '#eab308'
                                  : '#94a3b8',
                              border: '1px solid var(--bg-card, #fff)',
                            }}
                          />
                        )}
                      </div>
                    )}

                    {/* Color badge indicator (when no avatars but option has color) */}
                    {!showAvatars && opt.cor && (
                      <span style={{
                        width: 8, height: 8, borderRadius: '50%',
                        background: opt.cor, flexShrink: 0,
                        boxShadow: `0 0 0 2px ${opt.cor}25`,
                      }} />
                    )}

                    {/* Text block */}
                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                      <span
                        style={{
                          fontSize: '0.74rem',
                          fontWeight: 700,
                          color: (!showAvatars && opt.cor) ? opt.cor : 'var(--text-main, #0f172a)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {opt.nome}
                      </span>
                      {opt.subtitulo && (
                        <span
                          style={{
                            fontSize: '0.58rem',
                            color: 'var(--text-muted, #94a3b8)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {opt.subtitulo}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
